import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import TrialBanner from './TrialBanner';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Snackbar, Alert } from '@mui/material';
import { connectSocket } from '../../api/socket';
import DnsIcon from '@mui/icons-material/Dns';
import ChatIcon from '@mui/icons-material/Chat';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import SwapCallsIcon from '@mui/icons-material/SwapCalls';

const COLLAPSED_W = 72;
const EXPANDED_W = 240;

const MOBILE_TABS = [
  { label: 'Bots', icon: <DnsIcon />, path: '/app/dashboard' },
  { label: 'Flow', icon: <SwapCallsIcon />, path: '/app/flow-setup' },
  { label: 'Chats', icon: <ChatIcon />, path: '/app/chats' },
  { label: 'Analytics', icon: <BarChartIcon />, path: '/app/analytics' },
  { label: 'Settings', icon: <SettingsIcon />, path: '/app/settings' },
];

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });

  const [notifSnack, setNotifSnack] = useState({ open: false, message: '' });
  const [totalUnread, setTotalUnread] = useState(0);

  function playNotification() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1100, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.35);
    } catch { /* silent */ }
  }

  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const handler = (e) => setTotalUnread(e.detail);
    window.addEventListener('unreadUpdate', handler);
    return () => window.removeEventListener('unreadUpdate', handler);
  }, []);

  useEffect(() => {
    const sock = connectSocket();
    if (!sock) return;
    const doJoin = () => {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user?._id || user?._id;
      if (userId) sock.emit("join-agent", { agentId: userId, ownerId: userId });
    };
    if (sock.connected) doJoin();
    else sock.once("connect", doJoin);
    const handler = ({ chatbotId }) => {
      playNotification();
      const label = chatbotId?.startsWith('instagram_') ? 'Instagram' : 'Chat';
      setNotifSnack({ open: true, message: `📩 New ${label} message` });
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification('Talktokam', { body: `New ${label.toLowerCase()} message received` });
      }
    };
    sock.on("message-update", handler);
    return () => { sock.off("message-update", handler); };
  }, []);

  const toggleSidebar = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebarCollapsed', next);
      return next;
    });
  };

  const isUserPage =
    location.pathname.startsWith('/usertest') ||
    location.pathname.startsWith('/userchat');

  if (isUserPage) {
    return (
      <main style={{ padding: '20px' }}>
        <Outlet />
      </main>
    );
  }

  const sidebarW = collapsed ? COLLAPSED_W : EXPANDED_W;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#fff' }}>
      {!isMobile && (
        <aside style={{ width: sidebarW, flexShrink: 0, transition: 'width 0.3s ease' }}>
          <Sidebar collapsed={collapsed} onToggle={toggleSidebar} totalUnread={totalUnread} />
        </aside>
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header style={{ height: '64px' }}>
          <Header onMenuClick={toggleSidebar} />
        </header>

        <TrialBanner />

        <main style={{ flex: 1, overflowY: 'auto', background: '#f8faff', paddingBottom: isMobile ? 64 : 0 }}>
          <div className="water-bg" style={{ minHeight: '100%', padding: '10px' }}>
            <Outlet />
          </div>
        </main>

        {/* Mobile Bottom Nav */}
        {isMobile && (
          <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: 60, bgcolor: '#fff',
            borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-around',
            zIndex: 9999, boxShadow: '0 -2px 10px rgba(0,0,0,0.05)' }}>
            {MOBILE_TABS.map(tab => {
              const active = location.pathname === tab.path;
              return (
                <Box key={tab.path} onClick={() => navigate(tab.path)}
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', py: 0.5,
                    color: active ? '#6366f1' : '#9ca3af', transition: 'all 0.2s' }}>
                  <Box sx={{ fontSize: 22 }}>{tab.icon}</Box>
                  <Typography sx={{ fontSize: 9, fontWeight: active ? 700 : 500 }}>{tab.label}</Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </div>

      <Snackbar open={notifSnack.open} autoHideDuration={4000}
        onClose={() => setNotifSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="info" variant="filled" sx={{ borderRadius: 2 }}>
          {notifSnack.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Layout;
